import { clientLogTimes } from "../../lib/dayHelpers";
import type { LoggingFlowReviewItem } from "../../lib/logging-flow";
import {
  buildLogMealBatchArgs,
  defaultMealType,
  parseServingSize,
  type LogMealBatchArgs,
  type ReviewItem,
} from "../meal-review/review-utils";
import type { MealEstimateData } from "./card-data";

export type MealEstimateLogState =
  | { readonly kind: "idle" }
  | { readonly kind: "logging" }
  | { readonly kind: "logged" }
  | { readonly kind: "error"; readonly message: string };

function reviewItemsForLog(data: MealEstimateData): readonly ReviewItem[] {
  if (data.variant === "memory" && data.memoryMatch !== undefined) {
    const memory = data.memoryMatch;
    return [{
      key: "memory-match",
      name: memory.foodName,
      ...parseServingSize(memory.servingSize),
      calories: memory.calories,
      dataSource: "memory",
      ...(memory.confidence === undefined ? {} : { confidence: memory.confidence }),
    }];
  }
  return data.items.map((item, index) => ({
    key: `estimate-${index}`,
    ...item,
  } satisfies ReviewItem & LoggingFlowReviewItem));
}

export function buildMealEstimateLogArgs(
  data: MealEstimateData,
  now: number,
): LogMealBatchArgs {
  const { clientDayStart, clientLoggedAt } = clientLogTimes(now);
  const name = data.variant === "memory" ? data.memoryMatch?.foodName : data.mealName;
  return buildLogMealBatchArgs({
    date: clientDayStart,
    loggedAt: clientLoggedAt,
    mealType: defaultMealType(new Date(now).getHours()),
    ...(name === undefined ? {} : { name }),
    source: "chat",
    saveAsMemory: false,
    items: reviewItemsForLog(data),
  });
}

export async function submitMealEstimateLog(
  logMeal: (args: LogMealBatchArgs) => Promise<unknown>,
  data: MealEstimateData,
  now: number,
): Promise<MealEstimateLogState> {
  const args = buildMealEstimateLogArgs(data, now);
  try {
    await logMeal(args);
    console.log("[Frontend][MealEstimateCard] Meal logged directly", {
      mealType: args.mealType,
      itemCount: args.items.length,
      unknownKcalItems: args.items.filter((item) => item.kcalUnknown === true).length,
    });
    return { kind: "logged" };
  } catch (error: unknown) {
    console.error("[Frontend][MealEstimateCard] Direct meal log failed", {
      mealType: args.mealType,
      itemCount: args.items.length,
      error: error instanceof Error ? error.message : String(error),
    });
    return {
      kind: "error",
      message: "Could not log this meal. Check your connection and try again.",
    };
  }
}
